/**
 * LeaveUnsafeDialog - the way out
 * --------------------------------
 * What a student sees when they press "I do not feel safe". They may be
 * standing outside a stranger's house holding their phone, which is the only
 * thing that should decide how this screen is written.
 *
 * So:
 *
 * - **The reason is optional and says so.** Somebody frightened enough to
 *   leave should not have to compose an explanation before the button works.
 *   A required field here is one more obstacle at the worst possible moment,
 *   and the empty case tells us plenty on its own.
 *
 * - **112 comes first, in the largest text on the screen.** If the answer to
 *   this situation is the emergency services rather than a web form, the
 *   screen should say so before it asks for anything.
 *
 * - **It promises the two things they are actually weighing up**: that it
 *   will not count against them, and that the poster will not be told they
 *   reported anything. Both are true - see routes/applications.js - and both
 *   are exactly what somebody hesitates over.
 *
 * - **No confirmation step.** "Are you sure?" is the right pattern for a
 *   destructive action and the wrong one here. Leaving is the safe choice and
 *   the interface should not make them defend it.
 *
 * WHEN THE JOB IS ALREADY OVER (`done`), it is the same screen with the
 * leaving taken out: there is nothing to leave, so the promises about not
 * counting against them and the job going back on the marketplace would be
 * nonsense. What is left is the part that matters - 112 first, say what
 * happened, a moderator reads it today, and the person is not told. Added 18
 * September 2026 with the student's "work you have done" section, which is
 * where a finished job now lives.
 */

import { useState } from 'react';
import { ShieldAlert, Phone, AlertTriangle } from 'lucide-react';

import Modal from './Modal';
import { useI18n } from '../hooks/useI18n';

export default function LeaveUnsafeDialog({ open, chore, onClose, onConfirm, busy, done = false }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  /**
   * The description is required now - see the note on the /leave route in
   * backend/routes/applications.js for why the original "optional on purpose"
   * was reversed.
   *
   * The number matches MIN_UNSAFE_REASON on the server, which is what
   * actually enforces it. Written here too so somebody is told while they are
   * typing rather than refused after pressing the button - and if the two ever
   * disagree, the server decides.
   */
  const MIN_REASON = 40;
  const tooShort = reason.trim().length < MIN_REASON;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={done ? t('leave.reportTitle') : t('leave.title')}
      actions={
        <>
          <button type="button" className="btn btn-ghost btn-inline" onClick={onClose} disabled={busy}>
            {t('leave.goBack')}
          </button>
          <button
            type="button"
            className="btn btn-danger btn-inline"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || tooShort}
          >
            <ShieldAlert size={16} />
            {(() => {
              if (busy) return done ? t('leave.sending') : t('leave.leaving');
              return done ? t('leave.reportTitle') : t('leave.title');
            })()}
          </button>
        </>
      }
    >
      <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>
        <Phone size={20} aria-hidden="true" />
        <div>
          <strong>{t('leave.emergency')}</strong> {t('leave.emergencyBody')}
        </div>
      </div>

      {done ? (
        <>
          <p>
            {t('leave.aboutThisJob')}{' '}
            <strong>{chore?.title ? `"${chore.title}"` : t('leave.thisJob')}</strong>.{' '}
            {t('leave.overNowBody')}
          </p>

          <ul style={{ marginLeft: 'var(--space-md)', marginTop: 'var(--space-xs)' }}>
            <li>
              <strong>{t('leave.notTold')}</strong> {t('leave.notToldDoneBody')}
            </li>
            <li>
              <strong>{t('leave.readAtEight')}</strong> {t('leave.readAtEightBody')}
            </li>
          </ul>
        </>
      ) : (
        <>
          <p>
            {t('leave.youAreLeaving')}{' '}
            <strong>{chore?.title ? `"${chore.title}"` : t('leave.thisJob')}</strong>.{' '}
            {t('leave.noObligation')} {t('leave.noObligationBody')}
          </p>

          <ul style={{ marginLeft: 'var(--space-md)', marginTop: 'var(--space-xs)' }}>
            <li>
              <strong>{t('leave.noCount')}</strong> {t('leave.noCountBody')}
            </li>
            <li>
              <strong>{t('leave.notTold')}</strong> {t('leave.notToldBody')}
            </li>
            <li>
              <strong>{t('leave.readAtEight')}</strong> {t('leave.readAtEightBody')}
            </li>
          </ul>
        </>
      )}

      <div className="field" style={{ marginTop: 'var(--space-sm)' }}>
        <label htmlFor="leave-reason">{t('leave.whatHappened')} *</label>
        <p className="hint" style={{ marginTop: 0 }}>{t('leave.whatHappenedHint')}</p>
        <textarea
          id="leave-reason"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={2000}
          placeholder={t('leave.reasonPlaceholder')}
          aria-invalid={tooShort}
        />
        {/* Said while they type, not after they press. Somebody in the street
            outside a stranger's house should not meet a red error. */}
        {tooShort && <span className="hint">{t('leave.tooShort')}</span>}
        <p className="hint">{t('leave.onlyModerator')}</p>
      </div>

      {/* ---- WHAT THIS BUTTON IS NOT FOR ----
          Last, deliberately. Somebody who is actually frightened has already
          read the part that matters and pressed the button; this is for the
          person who is scrolling and weighing it up. Putting it above the
          reassurance would make a frightened child read a warning about
          misusing a safety exit before being told they can leave. */}
      {!done && (
        <div className="alert alert-warning" style={{ marginTop: 'var(--space-sm)' }}>
          <AlertTriangle size={18} aria-hidden="true" />
          <div className="text-sm">
            <strong>{t('leave.misuseTitle')}</strong> {t('leave.misuseBody')}
          </div>
        </div>
      )}
    </Modal>
  );
}
